import React, { useCallback, useEffect, useState } from "react";
import { Building2, ChevronDown, Check, Globe } from "lucide-react";
import { toast } from "react-toastify";
import api from "../api/axios";
import useAuthStore from "../store/useAuthStore";
import socketManager from "../api/socket";
import { plural } from "../utils/texto";

/**
 * Selector de residencial del SUPERADMINISTRADOR.
 *
 * Solo aparece para ese rol. Para cualquier otro no se dibuja nada: su
 * residencial sale del usuario en el servidor y no es elegible.
 *
 * QUÉ PASA AL CAMBIAR, y por qué en este orden:
 *
 *  1. Se guarda la elección (el cliente la manda en cada petición como
 *     `X-Residential-Id`; el servidor la ignora para los demás roles).
 *  2. Se RECONECTA el socket. Esto no es un detalle: la residencial del
 *     socket se fija al autenticar la conexión, así que una conexión abierta
 *     seguiría escuchando las salas de la residencial anterior y el
 *     superadministrador vería llegar avisos de una mientras mira otra.
 *  3. Se recarga la vista. Las pantallas piden sus datos al montarse; sin
 *     esto, la tabla seguiría mostrando lo de antes con el selector ya
 *     cambiado, que es la peor de las dos mentiras posibles.
 */
const SelectorResidencial = () => {
  const { user, residentialSeleccionada, seleccionarResidencial } = useAuthStore();
  const [residenciales, setResidenciales] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);

  const esSuperadmin = user?.role === "superadmin";

  const cargar = useCallback(async () => {
    if (!esSuperadmin) return;
    setCargando(true);
    try {
      // CON las desactivadas, a propósito. Pidiéndolas sin ellas, una
      // residencial desactivada que estuviera seleccionada no aparecía en la
      // lista, `actual` quedaba en `undefined` y la cabecera caía al rótulo
      // «Toda la plataforma» — mientras `X-Residential-Id` seguía viajando y
      // `/admin` mostraba los datos de esa sola comunidad. El rótulo no era
      // el problema: el superadministrador creía estar viendo la plataforma
      // entera y estaba viendo una.
      const { data } = await api.get("/residentials", {
        params: { incluirInactivas: "true" },
      });
      setResidenciales(data.residenciales || []);
    } catch {
      // El interceptor ya avisó.
    } finally {
      setCargando(false);
    }
  }, [esSuperadmin]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Cerrar al hacer clic fuera.
  useEffect(() => {
    if (!abierto) return undefined;
    const alClic = () => setAbierto(false);
    document.addEventListener("click", alClic);
    return () => document.removeEventListener("click", alClic);
  }, [abierto]);

  if (!esSuperadmin) return null;

  const actual = residenciales.find((r) => r._id === residentialSeleccionada);

  /*
   * El rótulo se decide por `residentialSeleccionada` y NO por si se encontró
   * el nombre. Son tres casos distintos y antes dos se confundían en uno:
   *
   *  · Sin selección: la plataforma completa. Es el único caso en que ese
   *    rótulo dice la verdad.
   *  · Con selección y con nombre: el nombre, y «(desactivada)» cuando lo
   *    está, porque una residencial desactivada tiene que verse como lo que
   *    es: sus datos siguen saliendo en `/admin`.
   *  · Con selección y sin nombre todavía —la lista aún no llegó, o esa
   *    residencial ya no existe—: se dice eso. Cualquier otra cosa sería
   *    afirmar que no hay filtro habiendo uno.
   */
  const hayFiltro = Boolean(residentialSeleccionada);
  let rotulo;
  if (!hayFiltro) rotulo = "Toda la plataforma";
  else if (actual) rotulo = actual.isActive ? actual.name : `${actual.name} (desactivada)`;
  else rotulo = cargando ? "Cargando…" : "Residencial no encontrada";

  const elegir = (id) => {
    setAbierto(false);
    if (id === residentialSeleccionada) return;

    seleccionarResidencial(id);

    // El socket lleva la residencial en su autenticación: hay que rehacerlo.
    try {
      socketManager.disconnect();
      socketManager.connect();
    } catch {
      /* si el tiempo real falla, la recarga de abajo lo reintenta */
    }

    const elegida = residenciales.find((r) => r._id === id);
    const nombre = elegida && !elegida.isActive ? `${elegida.name} (desactivada)` : elegida?.name;
    toast.info(nombre ? `Trabajando en ${nombre}` : "Viendo la plataforma completa");

    // Las pantallas cargan sus datos al montarse.
    setTimeout(() => window.location.reload(), 350);
  };

  return (
    <div style={estilos.contenedor} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        style={{ ...estilos.boton, ...(abierto ? estilos.botonAbierto : {}) }}
        onClick={() => setAbierto((v) => !v)}
        title={`Residencial en la que está trabajando: ${rotulo}`}
        aria-haspopup="listbox"
        aria-expanded={abierto}
      >
        {hayFiltro ? <Building2 size={16} /> : <Globe size={16} />}
        <span
          style={{
            ...estilos.texto,
            ...(actual && !actual.isActive ? estilos.textoInactiva : {}),
          }}
        >
          {rotulo}
        </span>
        <ChevronDown size={14} style={{ opacity: 0.7 }} />
      </button>

      {abierto && (
        <ul style={estilos.lista} role="listbox">
          <li>
            <button
              type="button"
              style={{
                ...estilos.opcion,
                ...(residentialSeleccionada ? {} : estilos.opcionActiva),
              }}
              onClick={() => elegir(null)}
            >
              <Globe size={15} />
              <span style={estilos.opcionTexto}>
                Toda la plataforma
                <small style={estilos.ayuda}>Solo para dar de alta residenciales</small>
              </span>
              {!residentialSeleccionada && <Check size={15} />}
            </button>
          </li>

          {cargando && <li style={estilos.vacio}>Cargando…</li>}

          {!cargando && residenciales.length === 0 && (
            <li style={estilos.vacio}>Todavía no hay residenciales</li>
          )}

          {residenciales.map((r) => (
            <li key={r._id}>
              <button
                type="button"
                style={{
                  ...estilos.opcion,
                  ...(r._id === residentialSeleccionada ? estilos.opcionActiva : {}),
                  ...(r.isActive ? {} : estilos.opcionInactiva),
                }}
                onClick={() => elegir(r._id)}
              >
                <Building2 size={15} />
                <span style={estilos.opcionTexto}>
                  {r.name}
                  <small style={estilos.ayuda}>
                    {r.code} · {r.usuarios} {plural(r.usuarios, "usuario")} · {r.visitas}{" "}
                    {plural(r.visitas, "visita")}
                    {r.isActive ? "" : " · desactivada"}
                  </small>
                </span>
                {r._id === residentialSeleccionada && <Check size={15} />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/*
 * ── COLORES: por qué aquí no hay ni un `var(--x, #fff)` ─────────────────────
 *
 * El desplegable salía con fondo blanco y texto gris claro en tema oscuro,
 * prácticamente ilegible. La causa no era el tema: era que pedía una variable
 * `--surface` que NO EXISTE en el sistema de diseño, así que el respaldo
 * literal `#fff` ganaba SIEMPRE y el panel era blanco en los dos temas. Las
 * variables de verdad son `--bg-elevated`, `--bg-tertiary`, `--text` y
 * `--text-secondary` (ver `src/index.css`).
 *
 * Van sin respaldo literal a propósito: un nombre mal escrito tiene que
 * romperse de forma visible, no quedarse en blanco para siempre.
 *
 * Y cada botón declara su `color`: con `color: inherit` el texto de las
 * opciones tomaba el de la cabecera, que no tiene por qué contrastar con el
 * fondo del panel.
 */
const estilos = {
  contenedor: { position: "relative" },
  boton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.45rem",
    padding: "0.45rem 0.8rem",
    borderRadius: 999,
    border: "1px solid var(--border)",
    background: "var(--bg-tertiary)",
    color: "var(--text)",
    cursor: "pointer",
    fontSize: "0.85rem",
    maxWidth: 300,
  },
  botonAbierto: { borderColor: "var(--gold)" },
  /* Más ancho que antes porque ahora el rótulo puede llevar «(desactivada)»
     detrás del nombre, y ése es justo el trozo que no se puede recortar. */
  texto: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 210,
  },
  /* Mismo criterio que `opcionInactiva`: se atenúa con el color del texto y
     no con `opacity`, para no bajar del contraste mínimo. */
  textoInactiva: { color: "var(--text-secondary)" },
  lista: {
    position: "absolute",
    top: "calc(100% + 0.4rem)",
    right: 0,
    minWidth: 280,
    maxHeight: 340,
    overflowY: "auto",
    margin: 0,
    padding: "0.35rem",
    listStyle: "none",
    borderRadius: 14,
    border: "1px solid var(--border)",
    background: "var(--bg-elevated)",
    color: "var(--text)",
    boxShadow: "var(--shadow-lg)",
    zIndex: 60,
  },
  opcion: {
    display: "flex",
    alignItems: "center",
    gap: "0.6rem",
    width: "100%",
    padding: "0.55rem 0.6rem",
    border: "none",
    borderRadius: 10,
    background: "transparent",
    cursor: "pointer",
    textAlign: "left",
    color: "var(--text)",
  },
  opcionActiva: { background: "var(--gold-subtle)" },
  /* Una residencial desactivada se atenúa con el color del texto y no con
     `opacity`: con opacidad el nombre bajaba del contraste mínimo, y es el
     dato que hay que poder leer para volver a activarla. */
  opcionInactiva: { color: "var(--text-secondary)" },
  opcionTexto: { display: "flex", flexDirection: "column", flex: 1, gap: "0.1rem" },
  ayuda: { fontSize: "0.72rem", color: "var(--text-secondary)" },
  vacio: { padding: "0.6rem", fontSize: "0.82rem", color: "var(--text-secondary)" },
};

export default SelectorResidencial;
